import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { useTranslation } from 'react-i18next';
import useForm from '@src/hooks/useForm';
import { createValidationSchema } from '@src/utils/form';
import { VOICE_NAME_REGEX } from '@src/constants/voiceCloning';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import AddSpeakerForm from './AddSpeakerForm';
import AddSpeakerActions from './AddSpeakerActions';

const AddSpeaker = ({
  open = false,
  onClose = () => {},
  speakerDialogData = null,
  isEdit = false,
  isApplyPreset = false,
  projectId,
  selectedBlockIds,
  onCloseWhenCreateVoice = () => {},
}) => {
  const { user } = useSelector((state) => state.auth);
  const { multiBlock } = useSelector((state) => state.project);
  const { blocks } = multiBlock || {};
  const { t } = useTranslation();
  const [openVoices, setOpenVoices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isApply = useRef(false);
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const handleCloseVoices = () => setOpenVoices(false);
  const handleCloseWhenCreateVoice = () => {
    handleCloseVoices();
    onCloseWhenCreateVoice();
  };
  const validationSchema = createValidationSchema()
    .required('name', t('fieldIsRequired', { fieldName: t('speakerName') }))
    .minLength('name', 5, t('speakerNameDescription'))
    .pattern('name', VOICE_NAME_REGEX, t('speakerNameDescription'))
    .maxLength('name', 25, t('speakerNameDescription'))
    .required('voice', t('fieldIsRequired', { fieldName: t('voice') }))
    .required('speed', t('fieldIsRequired', { fieldName: t('voiceSpeed') }))
    .toObject();

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    resetForm,
    setValues,
    handleBlur,
    setFieldError,
  } = useForm({
    initialValues: {
      name: '',
      voice: null,
      speed: 1,
    },
    options: {
      resetOnSubmit: false,
    },
    onSubmit: async (value) => {
      setIsLoading(true);
      try {
        let response;
        if (speakerDialogData?.id) {
          response = await apis.project.updateSpeaker(speakerDialogData?.id, {
            name: value.name?.trim(),
            voiceCode: value.voice.code,
            speed: value.speed,
          });
        } else {
          response = await apis.project.createSpeaker({
            name: value.name?.trim(),
            voiceCode: value.voice.code,
            speed: value.speed,
            projectId,
          });
        }
        if (response?.status) {
          const newSpeaker = response?.result?.speaker;
          if (newSpeaker && !speakerDialogData?.id) {
            dispatch(actions.project.addSpeaker({ ...value, ...newSpeaker }));
          } else if (speakerDialogData?.id) {
            dispatch(
              actions.project.updateSpeaker(speakerDialogData?.id, {
                ...value,
                ...newSpeaker,
              }),
            );
            const blocksUsingSpeaker = Object.keys(blocks || {}).filter(
              (id) => blocks[id]?.speakerId === speakerDialogData?.id,
            );
            const speakerBlockUpdates = (blocksUsingSpeaker || []).map(
              (blockIdToUpdate) => ({
                blockId: blockIdToUpdate,
                block: {
                  voiceCode: value?.voice?.code,
                  speed: value?.speed,
                  speakerId: newSpeaker?.id,
                  voice: value?.voice || {},
                },
              }),
            );
            if (speakerBlockUpdates.length) {
              dispatch(
                actions.project.updateMultipleBlocks(speakerBlockUpdates),
              );
            }
          }
          onClose();
          resetForm();
          if (isApply?.current && selectedBlockIds?.length > 0) {
            const blockUpdates = selectedBlockIds.map((blockIdToUpdate) => ({
              blockId: blockIdToUpdate,
              block: {
                voiceCode: value?.voice?.code,
                speed: value?.speed,
                speakerId: newSpeaker?.id,
                voice: value?.voice || {},
              },
            }));
            const options = undoRedoEnabled
              ? {
                  history: {
                    commit: true,
                    scope: HISTORY_SCOPE.DUBBING,
                    label: HISTORY_LABEL.CHANGE_BLOCK_VOICE,
                  },
                }
              : {};
            dispatch(
              actions.project.updateMultipleBlocks(blockUpdates, options),
            );
          } else {
            dispatch(
              actions.noti.push({
                severity: 'success',
                message: t('saveProjectSuccessful'),
              }),
            );
          }
        } else if (response?.errorCode === ERROR_CODE.SPEAKER_NAME_EXISTED) {
          setFieldError('name', t('speakerNameExisted'));
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('errorOccurred'),
          }),
        );
      }
    },
    validationSchema,
  });
  useEffect(() => {
    if (speakerDialogData && open) {
      setValues({
        name: speakerDialogData?.name,
        voice: speakerDialogData?.voice,
        speed: speakerDialogData?.speed,
      });
    }
  }, [speakerDialogData, open]);

  const handleChangeVoice = (newVoice) => setFieldValue('voice', newVoice);

  const handleFieldChange = (field, value) => {
    setFieldValue(field, value);
  };

  const handleFieldBlur = (field) => {
    handleBlur(field);
  };

  const onSubmitVoicePreset = () => {
    handleSubmit();
  };

  const onSaveVoicePreset = () => {
    isApply.current = false;
    onSubmitVoicePreset();
  };

  const onSaveAndApplyVoicePreset = () => {
    isApply.current = true;
    onSubmitVoicePreset();
  };

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    resetForm();
    onClose();
  };

  const renderContent = () => (
    <AddSpeakerForm
      values={values}
      errors={errors}
      isEdit={isEdit}
      user={user}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      onOpenVoices={() => setOpenVoices(true)}
      t={t}
    />
  );

  const renderAction = () => (
    <AddSpeakerActions
      isEdit={isEdit}
      isApplyPreset={isApplyPreset}
      isLoading={isLoading}
      isApplying={isApply.current}
      onCancel={handleClose}
      onSave={onSaveVoicePreset}
      onSaveAndApply={onSaveAndApplyVoicePreset}
      t={t}
    />
  );

  return (
    <>
      <CommonDialog
        width="640px"
        fullWidth
        open={open}
        onClose={handleClose}
        title={isEdit ? t('editSpeakerConfiguration') : t('addSpeaker')}
        contentComponent={renderContent()}
        actionComponent={renderAction()}
      />
      {openVoices && (
        <VoiceSelectionDialog
          open={openVoices}
          activeVoiceId={values.voice?.id}
          activeVoiceCode={values.voice?.code}
          onClose={handleCloseVoices}
          onChangeVoice={handleChangeVoice}
          hasLanguageFilter
          isApplyPreset
          voiceStatus={values.voice?.status}
          onCloseWhenCreateVoice={handleCloseWhenCreateVoice}
          isDubbing
          multiLanguage
        />
      )}
    </>
  );
};

export default AddSpeaker;
